import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { ArrowRightIcon } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ZodObject } from 'zod';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton, PageHeadingButton } from '@/components/ui/page-heading';
import { useProgress } from '@/context/progress';
import { type RouteConfig, useRouteConfig } from '@/context/route-config';
import { APPROPRIATENESS_TEST_DEFAULTS, type AppropriatenessTest, appropriatenessTestWithDefaultsSchema } from '@/features/futures/schemas/appropriateness-test';
import { getFirstUnansweredStep, getStepIndex, hasUnansweredSteps } from '@/features/futures/utils/appropriateness-test';
import { useProfileClientCategorisationTreatment } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useProfileFuturesAppropriateness, useProfileFuturesAppropriatenessMutation } from '@/hooks/queries/use-profile-futures-appropriateness';
import { CURRENT_VERSION } from '@/services/api/profile/futures-appropriateness';
import { STEP_LEGENDS, STEP_SCHEMAS, STEPS } from './appropriateness-test-form-steps';

export interface FuturesAppropriatenessTestRouteConfig extends RouteConfig {
  appropriatenessTestResult: string;
  clientCategorisationTreatment: string;
}

const FuturesAppropriatenessTestFormCurrentStep = () => {
  const { setProgress } = useProgress();
  const { currentStep, setStep, totalSteps } = useGuardedFormStepper<AppropriatenessTest>();
  const [searchParams, setSearchParams] = useSearchParams();
  const Step = STEPS[currentStep];
  const isRetake = searchParams.get('retake') === 'true';

  useEffect(() => {
    if (currentStep === 0 && isRetake) {
      setSearchParams(undefined, { replace: true });
      setStep(getStepIndex('futuresKnowledgeStatements'));
    }
  }, [currentStep, isRetake, setSearchParams, setStep]);

  useEffect(() => {
    return setProgress(currentStep / totalSteps);
  }, [currentStep, setProgress, totalSteps]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
FuturesAppropriatenessTestFormCurrentStep.displayName = 'FuturesAppropriatenessTestFormCurrentStep';

const FuturesAppropriatenessTestFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<AppropriatenessTest>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
FuturesAppropriatenessTestFormCurrentStepLegend.displayName = 'FuturesAppropriatenessTestFormCurrentStepLegend';

const FuturesAppropriatenessTestFormBackButton = () => {
  const { trigger } = useFormContext<AppropriatenessTest>();
  const { currentStep, prevStep } = useGuardedFormStepper<AppropriatenessTest>();
  const Step = STEPS[currentStep];

  const handlePrevStep = useCallback(async () => {
    const shouldValidate = Step && 'validateOnNavigation' in Step && Step.validateOnNavigation;
    if (shouldValidate && !(await trigger())) {
      return;
    }

    prevStep();
  }, [prevStep, Step, trigger]);

  if (currentStep === 0) return null;

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
FuturesAppropriatenessTestFormBackButton.displayName = 'FuturesAppropriatenessTestFormBackButton';

const FuturesAppropriatenessTestGoToSummaryButton = () => {
  const { trigger } = useFormContext<AppropriatenessTest>();
  const { currentStep, isFirstStep, isLastStep, setStep } = useGuardedFormStepper<AppropriatenessTest>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  const handleGoToSummary = useCallback(async () => {
    const shouldValidate = Step && 'validateOnNavigation' in Step && Step.validateOnNavigation;
    if (shouldValidate && !(await trigger())) {
      return;
    }

    setStep(getStepIndex('confirmation'));
  }, [setStep, Step, trigger]);

  if (isFirstStep || isLastStep) return null;

  return (
    <PageHeadingButton floatingInPage="right" onClick={handleGoToSummary}>
      {t('FuturesAppropriatenessAssessment.goToSummary')}
      <ArrowRightIcon className="size-4" />
    </PageHeadingButton>
  );
};
FuturesAppropriatenessTestGoToSummaryButton.displayName = 'FuturesAppropriatenessTestGoToSummaryButton';

const FuturesAppropriatenessTestFormFooter = ({ isPending, resumeFromStep }: { isPending: boolean; resumeFromStep: number | undefined }) => {
  const { currentStep, isFirstStep, isLastStep, nextStep, setStep } = useGuardedFormStepper<AppropriatenessTest>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  const handleNextStep = useCallback(() => {
    if (isFirstStep && typeof resumeFromStep === 'number') {
      setStep(resumeFromStep);
      return;
    }

    const isSubmittableStep = STEP_SCHEMAS[currentStep] instanceof ZodObject ? Object.keys(STEP_SCHEMAS[currentStep].shape).length > 0 : false;

    nextStep({ submit: isSubmittableStep });
  }, [currentStep, isFirstStep, nextStep, resumeFromStep, setStep]);

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={handleNextStep}>
        <span className={isPending ? 'invisible' : ''}>
          {isFirstStep ? t(`FuturesAppropriatenessAssessment.${typeof resumeFromStep === 'number' ? 'resume' : 'start'}`) : t(`buttons.${isLastStep ? 'submit' : 'next'}`)}
        </span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
FuturesAppropriatenessTestFormFooter.displayName = 'FuturesAppropriatenessTestFormFooter';

const FuturesAppropriatenessTestForm = () => {
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const { isLoading: isLoadingFuturesAppropriateness, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const { isPending, mutateAsync } = useProfileFuturesAppropriatenessMutation();
  const routeConfig = useRouteConfig<FuturesAppropriatenessTestRouteConfig>();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingClientCategorisationTreatment || isLoadingFuturesAppropriateness;

  const onError = () => {
    toast({
      description: t('fundamentals.defaultError.message'),
      variant: 'destructive',
    });
  };

  const onSubmit = async (data: AppropriatenessTest) => {
    const { confirmation, ...answers } = data;

    try {
      await mutateAsync({
        answers,
        confirmed: confirmation ?? false,
        date: futuresAppropriateness?.date ?? new Date().toISOString(),
      });
    } catch (error) {
      onError();

      throw error;
    }
  };

  if (isLoading) return <LoadingCard />;

  if (!clientCategorisationTreatment) return <Navigate to={routeConfig.clientCategorisationTreatment} replace />;

  if (futuresAppropriateness && 'result' in futuresAppropriateness) return <Navigate to={routeConfig.appropriatenessTestResult} replace />;

  // A draft started on an earlier version holds answers to different questions, so it restarts on the current one
  const isCurrentVersionDraft = futuresAppropriateness?.version === CURRENT_VERSION;
  const defaultValues = appropriatenessTestWithDefaultsSchema.parse((isCurrentVersionDraft && futuresAppropriateness?.answers) || APPROPRIATENESS_TEST_DEFAULTS);

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} mode="onSubmit" steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <FuturesAppropriatenessTestFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <FuturesAppropriatenessTestFormBackButton />
          {futuresAppropriateness?.date && !hasUnansweredSteps(defaultValues) ? <FuturesAppropriatenessTestGoToSummaryButton /> : null}
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <FuturesAppropriatenessTestFormCurrentStep />
          <FuturesAppropriatenessTestFormFooter isPending={isPending} resumeFromStep={futuresAppropriateness?.date ? getFirstUnansweredStep(defaultValues) : undefined} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
FuturesAppropriatenessTestForm.displayName = 'FuturesAppropriatenessTestForm';

export default FuturesAppropriatenessTestForm;
